import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

/**
 * Products section navigation.
 *
 * The URL is the single source of truth for which products screen is showing:
 *
 *   /suspire-terminal/products/manage            Manage Products list
 *   /suspire-terminal/products/manage/:id        ...with that product open
 *   /suspire-terminal/products/pending           Pending approval queue
 *   /suspire-terminal/products/pending/:id       ...with that product under review
 *   /suspire-terminal/products/unlisted[/:id]    Unlisted products
 *   /suspire-terminal/products/add[/manual|bulk] Add products
 *
 * Opening a product pushes a history entry, so browser Back closes it and lands
 * on the same list (still mounted, so filters / page / sub-tab survive). The
 * in-app close (× / Cancel) goes "up" to the parent list: it pops the entry when
 * we came from that list, and replaces it when the product was deep-linked.
 */

export const PRODUCTS_BASE_PATH = '/suspire-terminal/products';

export const PRODUCT_SECTIONS = ['manage', 'pending', 'unlisted', 'add'];

export const productsPath = (section, child) =>
  child ? `${PRODUCTS_BASE_PATH}/${section}/${child}` : `${PRODUCTS_BASE_PATH}/${section}`;

// Reads the `products/*` splat as `{ section, child }`.
export function useProductsRouteParams() {
  const { '*': rest = '' } = useParams();
  const [section = '', child = null] = rest.split('/').filter(Boolean);
  return { section, child };
}

const LEGACY_TABS = {
  'manage-products': 'manage',
  'approve-products': 'pending',
  'unlisted-products': 'unlisted',
  'add-products': 'add',
};

/**
 * Translates pre-routing links into their canonical path, or null if the URL
 * isn't a legacy one. Still in the wild: email links
 * (`/suspire-terminal/products?product_id=…`) and the old hash tabs
 * (`#?tab=approve-products&product_id=…`, `#/shipturtle?tab=add-products&method=manual`).
 */
export function resolveLegacyProductsUrl(search, hash) {
  const hashParams = new URLSearchParams(hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '');
  const searchParams = new URLSearchParams(search);
  const read = (name) => hashParams.get(name) || searchParams.get(name);

  const tab = read('tab');
  const productId = read('product_id') || read('productId');
  if (!tab && !productId) return null;

  const section = LEGACY_TABS[tab] || 'manage';
  if (section === 'add') {
    const method = read('method');
    return productsPath('add', method === 'manual' || method === 'bulk' ? method : null);
  }
  return productsPath(section, productId);
}

/**
 * The child screen of one products section (a product id, or the add method),
 * plus helpers to open and close it with correct history behaviour.
 */
export function useProductsChildRoute(section) {
  const navigate = useNavigate();
  const location = useLocation();
  const { section: currentSection, child } = useProductsRouteParams();
  const childId = currentSection === section ? child : null;

  // Async callers (a save resolving after the user moved on, a delayed close)
  // read the live route through this ref instead of a stale closure.
  const latest = useRef(null);
  latest.current = {
    childId,
    fromList: Boolean(location.state?.fromList),
    search: location.search,
  };

  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Guards against a double close popping two history entries.
  const closing = useRef(null);
  useEffect(() => {
    closing.current = null;
  }, [childId]);

  const openChild = useCallback((id) => {
    const current = latest.current;
    if (!id || !mounted.current || current.childId === id) return;
    navigate(
      { pathname: productsPath(section, id), search: current.search },
      current.childId
        ? { replace: true, state: { fromList: current.fromList } }
        : { state: { fromList: true } },
    );
  }, [navigate, section]);

  // Pass the id to make the close conditional on that child still being open —
  // e.g. a delayed close from a detail view that has since been replaced.
  const closeChild = useCallback((id) => {
    const current = latest.current;
    if (!mounted.current || !current.childId || closing.current === current.childId) return;
    if (id && id !== current.childId) return;
    closing.current = current.childId;
    if (current.fromList) {
      navigate(-1);
    } else {
      navigate({ pathname: productsPath(section), search: current.search }, { replace: true });
    }
  }, [navigate, section]);

  // True while `id` is still the screen in the URL — lets an async load or a
  // delayed redirect bail out if the user has already navigated elsewhere.
  const isCurrentChild = useCallback(
    (id) => mounted.current && latest.current.childId === id,
    [],
  );

  return { childId, openChild, closeChild, isCurrentChild };
}
